/**
 * Refusals that name themselves (N03, re-audit 2026-08-08).
 *
 * The upload surfaces learned this once (F44 -> `src/attachment-upload.ts`)
 * and the partner console learned it again (P-06), but the ordinary FORMS
 * never did: twelve client places rendered a bare `common.error`
 * ("Neco se zaseklo. Zkus to prosim znovu.") over a server that had just
 * NAMED the reason. Measured live on the deployed dev worker: saving a
 * seventh Google account answers 400 `too_many_accounts` and an invite over
 * the seat count answers 409 `capacity_reached`, and neither is fixed by
 * repeating the click - the client would press a button that refuses forever
 * and never learn why.
 *
 * So the reason travels: the caller reads the body, maps the code to an
 * ISSUE here, and the surface prints the issue's own sentence. A permanent
 * refusal says what happened and what to do instead; only a genuinely
 * repeatable failure may invite a repeat, and `actionIssuePermanent` is what
 * hides a "try again" control that could not work.
 */

export type ActionIssue =
  /** Know-how Google accounts: the sixth is the last one (MAX_GOOGLE_ACCOUNTS). */
  | "too_many_accounts"
  /** Invites: the client's seats are used up. */
  | "capacity_reached"
  /** The step moved under the client (already submitted, already accepted). */
  | "state_conflict"
  /** The app release waits for the approved sales page. */
  | "salespage_approval_required"
  /** The podnět form: the trial Buddy is not published yet. Ours to finish. */
  | "trial_not_ready"
  /** The podnět form: tester attribution opens once the bot has changed hands. */
  | "tester_before_transfer"
  /** Handover step 3: the instance owner role is not set yet (F118). */
  | "owner_role_pending"
  /**
   * F134b: the instance has changed hands. Not a step that is early - a step
   * that is over: we no longer administer the instance, so there is nothing to
   * send a podnět about.
   */
  | "ownership_transferred"
  /** Apple: the D-U-N-S number has to be confirmed first. */
  | "duns_required"
  /**
   * F147: the questionnaire step itself is not open yet. Its own issue, apart
   * from `forbidden`, because the client did nothing wrong and the page has to
   * name the step that IS open instead of a permission problem.
   */
  | "questionnaire_locked"
  /** F147: the step is open; the profile confirmation stands in front of it. */
  | "profile_confirmation_required"
  /** The form sent something the server cannot read. */
  | "invalid_request"
  /** This session may not do it (wrong role, someone else's client). */
  | "forbidden"
  /** The thing is gone (deleted version, revoked invite). */
  | "not_found"
  /** Too many attempts in a row. */
  | "rate_limited"
  /** The server broke. Repeating is genuinely worth it. */
  | "server"
  /** The browser never reached the server. */
  | "offline"
  /** Everything the server did not name. */
  | "generic";

/**
 * Refusals a repeat cannot clear. The sentence names the way out instead of
 * inviting a second identical click, and a retry CONTROL beside it is hidden.
 */
const PERMANENT: ReadonlySet<ActionIssue> = new Set<ActionIssue>([
  "too_many_accounts",
  "capacity_reached",
  "state_conflict",
  "salespage_approval_required",
  "owner_role_pending",
  // F134b: nothing clears this one - the transfer is one-way from our side.
  "ownership_transferred",
  "duns_required",
  "invalid_request",
  "forbidden",
  "not_found",
  // F114: both clear when somebody else's step finishes - the team publishes
  // the trial chat, the bot changes hands - never by pressing Odeslat again.
  "trial_not_ready",
  "tester_before_transfer",
  // F147: neither clears by repeating - one waits for an earlier step, the
  // other for one named click on the profile page.
  "questionnaire_locked",
  "profile_confirmation_required"
]);

export function actionIssuePermanent(issue: ActionIssue): boolean {
  return PERMANENT.has(issue);
}

const NAMED_CODES: ReadonlySet<string> = new Set<string>([
  "too_many_accounts",
  "capacity_reached",
  "state_conflict",
  "salespage_approval_required",
  "owner_role_pending",
  "ownership_transferred",
  "duns_required",
  "invalid_request",
  "forbidden",
  "not_found",
  "trial_not_ready",
  "tester_before_transfer",
  "questionnaire_locked",
  "profile_confirmation_required"
]);

/**
 * The server's own word wins; the status code only fills the gaps. A code the
 * client copy does not know yet falls through to `generic` rather than
 * printing a raw internal string at the client (the Servis law, one floor
 * down: `missing_default_partner` is not a sentence).
 */
export function actionIssueFromResponse(status: number, error?: string | null): ActionIssue {
  const code = typeof error === "string" ? error.trim() : "";
  if (NAMED_CODES.has(code)) return code as ActionIssue;
  if (status === 429) return "rate_limited";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "state_conflict";
  if (status === 400 || status === 422) return "invalid_request";
  if (status >= 500) return "server";
  return "generic";
}

/** Reasons the browser can name before any server answer. */
export function actionIssueFromNetwork(online: boolean): ActionIssue {
  return online ? "generic" : "offline";
}

const isOnline = (): boolean => (typeof navigator === "undefined" ? true : navigator.onLine);

/**
 * The ONE way a fetch answer becomes an issue: reads the body once (a body
 * that is not JSON is not an error message), and never throws - a caller in a
 * `catch` has already lost the code.
 */
export async function actionIssueFrom(response: Response): Promise<ActionIssue> {
  return actionIssueFromResponse(response.status, await readErrorCode(response));
}

/**
 * The server's own word, raw. A caller needs it when the CODE decides
 * something the issue cannot express - F150's player tells the read-only
 * gate's refusal (which the impersonation bar already names) apart from a
 * genuine `forbidden`, and both map to the same issue. Reads the body ONCE,
 * like `actionIssueFrom`, and never throws.
 */
export async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const body = await response.json() as { error?: unknown } | null;
    if (body && typeof body.error === "string") return body.error;
  } catch {
    // No JSON body: the status is all we have.
  }
  return null;
}

/** Thrown-away fetches (network down, aborted) still get their own reason. */
export function actionIssueFromThrown(): ActionIssue {
  return actionIssueFromNetwork(isOnline());
}

/**
 * One vocabulary for every client form. The key is built from the issue, so
 * `check:locales` cannot see it: `tests/fix2-chyby.test.ts` walks every
 * `ActionIssue` in BOTH locales instead (the wave-2 law for dynamic keys).
 */
export function actionIssueLocaleKey(issue: ActionIssue): string {
  return `common.actionError.${issue}`;
}
